/**
 * The typed fetch wrapper. No axios — this is the whole client (tech stack §1).
 *
 * Every response is parsed through the schema from `@console/types`, so a
 * backend that drifts from the shared contract fails here, loudly, rather than
 * rendering `undefined` somewhere three components deep.
 */

const API_BASE = '/api/v1'

export class ApiError extends Error {
  readonly status: number

  constructor(message: string, status: number, options?: { cause?: unknown }) {
    super(message, options)
    this.name = 'ApiError'
    this.status = status
  }
}

interface Parser<T> {
  parse: (value: unknown) => T
}

interface RequestOptions {
  /**
   * Status codes that carry a valid body rather than an error. `/health`
   * answers 503 with a well-formed body naming the failed dependency, and that
   * body is the point — the caller wants to render it, not throw it away.
   */
  alsoAccept?: number[]
}

/**
 * The backend's error responses are always `{ error: string, statusCode }`
 * (`errorResponseSchema` in `@console/types`). When a failed response is
 * shaped that way, its `error` string becomes the thrown message directly —
 * "Invalid email or password." rather than a generic "Request failed." — so
 * a form can render `error.message` straight to the user without a switch
 * statement mapping status codes to copy.
 */
async function errorMessageFrom(response: Response, path: string): Promise<string> {
  try {
    const body: unknown = await response.json()
    if (
      typeof body === 'object' &&
      body !== null &&
      'error' in body &&
      typeof body.error === 'string'
    ) {
      return body.error
    }
  } catch {
    // Not JSON, or not the expected shape — fall through to the generic message.
  }
  return `Request to ${path} failed.`
}

interface FetchInit {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE'
  headers?: Record<string, string>
  body?: string
}

async function doFetch(
  path: string,
  init: FetchInit,
  { alsoAccept = [] }: RequestOptions,
): Promise<Response> {
  let response: Response

  try {
    response = await fetch(`${API_BASE}${path}`, {
      credentials: 'same-origin',
      method: init.method,
      headers: { accept: 'application/json', ...init.headers },
      // `exactOptionalPropertyTypes` treats `body: undefined` as distinct
      // from omitting `body` entirely, and only the latter satisfies
      // `RequestInit` — so the key is spread in, not always present.
      ...(init.body !== undefined && { body: init.body }),
    })
  } catch (cause) {
    // Status 0: the request never reached the server. Distinct from a server
    // that answered with a failure, and the operator's next check differs.
    throw new ApiError('Could not reach the console server.', 0, { cause })
  }

  if (!response.ok && !alsoAccept.includes(response.status)) {
    throw new ApiError(await errorMessageFrom(response, path), response.status)
  }

  return response
}

export async function apiGet<T>(
  path: string,
  schema: Parser<T>,
  options: RequestOptions = {},
): Promise<T> {
  const response = await doFetch(path, { method: 'GET' }, options)
  const payload: unknown = await response.json()
  return schema.parse(payload)
}

/**
 * `csrfToken` is required on every call, deliberately — there is no
 * "unprotected mutation" escape hatch here. Callers fetch it via
 * `fetchCsrfToken` (see `auth/api.ts`) immediately before the mutating
 * request; the backend validates it as the double-submit cookie pattern
 * (UI spec addendum, tech stack §4).
 */
async function apiMutate<T>(
  method: 'POST' | 'PUT' | 'DELETE',
  path: string,
  body: unknown,
  csrfToken: string,
  schema: Parser<T>,
  options: RequestOptions = {},
): Promise<T> {
  // `logout` and every DELETE post no body at all. `JSON.stringify(undefined)`
  // returns the *value* `undefined`, not the string `"undefined"` — so a
  // naive unconditional stringify still sends `content-type: application/json`
  // with no body, which Fastify rejects outright (`FST_ERR_CTP_EMPTY_JSON_BODY`,
  // 400). Both the header and the body key are omitted together, matching
  // what a bodyless request actually is.
  const hasBody = body !== undefined

  const response = await doFetch(
    path,
    {
      method,
      headers: {
        'x-csrf-token': csrfToken,
        ...(hasBody && { 'content-type': 'application/json' }),
      },
      ...(hasBody && { body: JSON.stringify(body) }),
    },
    options,
  )

  if (response.status === 204) {
    return schema.parse(undefined)
  }

  const payload: unknown = await response.json()
  return schema.parse(payload)
}

export function apiPost<T>(
  path: string,
  body: unknown,
  csrfToken: string,
  schema: Parser<T>,
  options: RequestOptions = {},
): Promise<T> {
  return apiMutate('POST', path, body, csrfToken, schema, options)
}

/**
 * PUT rather than POST for the workspace switch: it replaces the session's one
 * current selection with another, and doing it twice with the same id leaves
 * the same state. POST would suggest each call creates something.
 */
export function apiPut<T>(
  path: string,
  body: unknown,
  csrfToken: string,
  schema: Parser<T>,
  options: RequestOptions = {},
): Promise<T> {
  return apiMutate('PUT', path, body, csrfToken, schema, options)
}

export function apiDelete<T>(
  path: string,
  csrfToken: string,
  schema: Parser<T>,
  options: RequestOptions = {},
): Promise<T> {
  return apiMutate('DELETE', path, undefined, csrfToken, schema, options)
}
